import { useState, useEffect } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Badge } from '../ui/badge'
import { Code2 } from 'lucide-react'
import { api } from '../../lib/api'

interface VariableDefinition {
  key: string
  displayName: string
  description: string
  category: 'time' | 'firm' | 'lead' | 'call' | 'custom'
  resolvedAt: 'save' | 'call'
  example: string
}

interface VariablePickerProps {
  onInsert: (variable: string) => void
}

const CATEGORY_LABELS: Record<string, string> = {
  time: 'Time',
  firm: 'Firm',
  lead: 'Lead',
  call: 'Call Context',
  custom: 'Custom',
}

const CATEGORY_ORDER = ['firm', 'lead', 'call', 'time', 'custom']

export function VariablePicker({ onInsert }: VariablePickerProps) {
  const [open, setOpen] = useState(false)
  const [variables, setVariables] = useState<VariableDefinition[]>([])
  const [customVars, setCustomVars] = useState<VariableDefinition[]>([])

  useEffect(() => {
    if (!open) return
    api.get('/api/voice/variables').then(res => {
      if (res.data.success) {
        setVariables(res.data.builtInVariables || [])
        // Convert custom variables to VariableDefinition format
        const custom = (res.data.customVariables || []).map((cv: any) => ({
          key: cv.key,
          displayName: cv.displayName,
          description: `Custom variable (${cv.resolvedAt === 'save' ? 'baked at save' : 'resolved at call time'})`,
          category: 'custom' as const,
          resolvedAt: cv.resolvedAt,
          example: cv.value,
        }))
        setCustomVars(custom)
      }
    }).catch(() => {})
  }, [open])

  const allVars = [...variables, ...customVars]
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const vars = allVars.filter(v => v.category === cat)
    if (vars.length > 0) acc[cat] = vars
    return acc
  }, {} as Record<string, VariableDefinition[]>)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
          title="Insert a variable"
        >
          <Code2 className="h-3 w-3" />
          <span>{'{{var}}'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 max-h-80 overflow-y-auto" align="start">
        <div className="p-2 border-b">
          <span className="text-xs font-medium text-muted-foreground">Insert Variable</span>
        </div>
        {Object.entries(grouped).map(([category, vars]) => (
          <div key={category}>
            <div className="px-3 py-1.5 bg-muted/50 border-b">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {CATEGORY_LABELS[category] || category}
              </span>
            </div>
            {vars.map(v => (
              <button
                key={v.key}
                className="w-full text-left px-3 py-1.5 hover:bg-accent transition-colors flex items-center justify-between group"
                onClick={() => {
                  onInsert(`{{${v.key}}}`)
                  setOpen(false)
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <code className="text-xs font-mono text-primary">{`{{${v.key}}}`}</code>
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      {v.resolvedAt === 'save' ? 'save' : 'call'}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{v.description}</p>
                </div>
              </button>
            ))}
          </div>
        ))}
        {Object.keys(grouped).length === 0 && (
          <div className="p-4 text-center text-xs text-muted-foreground">Loading variables...</div>
        )}
      </PopoverContent>
    </Popover>
  )
}
